import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, PageHeader, SectionLabel } from '../components/ui';
import { candidatesAPI } from '../lib/api';
import { fmtDateShort } from '../lib/utils';

interface FinanceData {
  totals: {
    total_raised?: string | number | null;
    contribution_count?: string | number | null;
    last_transaction_date?: string | null;
  };
  contributions: Array<{
    contributor_name?: string | null;
    contributor_city?: string | null;
    contributor_state?: string | null;
    amount?: string | number | null;
    transaction_date?: string | null;
    contribution_type?: string | null;
  }>;
}

const usd = (v: string | number | null | undefined) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (n == null || Number.isNaN(n)) return '$0';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

export default function CandidateFinanceScreen() {
  const navigate = useNavigate();
  const { id = '' } = useParams();

  const { data, isLoading } = useQuery<FinanceData | null>(['citizen-candidate-finance', id], async () => {
    const { data } = await candidatesAPI.getCampaignFinance(id);
    return data.data;
  });

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader title="Campaign Finance" onBack={() => navigate(-1)} />

      <div className="flex-1 px-6 pt-2 pb-24">
        {isLoading && <p className="text-sm text-[color:var(--muted-foreground)] py-10">Loading…</p>}

        {!isLoading && !data && (
          <Card className="p-5 border-0 text-center" style={{ background: 'transparent', borderStyle: 'dashed', borderWidth: 1 }}>
            <p className="text-sm text-[color:var(--muted-foreground)]">
              No campaign finance data on file for this candidate yet.
            </p>
          </Card>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Card className="p-4 border-0">
                <div className="text-2xl font-extrabold" style={{ color: 'var(--primary)' }}>
                  {usd(data.totals?.total_raised)}
                </div>
                <p className="text-[11px] text-[color:var(--muted-foreground)] font-bold uppercase tracking-widest mt-1">
                  Total raised
                </p>
              </Card>
              <Card className="p-4 border-0">
                <div className="text-2xl font-extrabold" style={{ color: 'var(--primary)' }}>
                  {data.totals?.contribution_count || 0}
                </div>
                <p className="text-[11px] text-[color:var(--muted-foreground)] font-bold uppercase tracking-widest mt-1">
                  Contributions
                </p>
              </Card>
            </div>

            {data.totals?.last_transaction_date && (
              <p className="text-xs text-[color:var(--muted-foreground)] mb-5">
                Last reported {fmtDateShort(data.totals.last_transaction_date)}
              </p>
            )}

            {data.contributions.length > 0 && (
              <>
                <SectionLabel>Recent contributions</SectionLabel>
                <Card className="divide-y border-0 overflow-hidden">
                  {data.contributions.map((c, i) => (
                    <div key={i} className="p-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{c.contributor_name || 'Anonymous'}</p>
                        <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">
                          {[c.contributor_city, c.contributor_state].filter(Boolean).join(', ')}
                          {c.transaction_date ? ` · ${fmtDateShort(c.transaction_date)}` : ''}
                        </p>
                      </div>
                      <span className="text-sm font-bold tabular-nums shrink-0" style={{ color: 'var(--primary)' }}>
                        {usd(c.amount)}
                      </span>
                    </div>
                  ))}
                </Card>
                <p className="text-[11px] text-center mt-3 text-[color:var(--muted-foreground)]">
                  Public campaign finance data from the Colorado Secretary of State.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
